import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import { Layout } from '@components';
import {
  ArrowRightIcon,
  Button,
  Description,
  Field,
  Heading,
  Label,
  Link,
  Paragraph,
  Select,
} from '@quickmd-llc/design-system';
import { US_STATES } from '@constants';
import { useApplicationStore } from '@data';
import { Events, tracking } from '@lib/events';
import { buildLeadSessionId } from '@utils';
import { useIsLoggedIn } from '@hooks/useIsLoggedIn';
import { getVisitTypeUrl, getVisitTypeUrlForPatient } from '@router/utils';
import { AvailableServices } from './components';
import { getHeaderText } from './utils';
import { usePatientWebUrl } from 'data/slices/devTools/hooks/usePatientWebUrl';

export const DEFAULT_SELECT_VALUE = 'default';

export default function Services() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { serviceTypeId } = useParams();
  const {
    leadSessionId,
    setLeadSessionId,
    setShouldSearchNextAvailability,
    setStateCode,
    stateCodeValue = DEFAULT_SELECT_VALUE,
    utmParams,
    flagsHaveBeenFetched,
    experimentCuratedAppointmentSlotsEnabled,
    hasCuratedAppointmentSlotsFlag,
    patientProfile,
  } = useApplicationStore(
    useShallow((state) => ({
      leadSessionId: state.leadSessionId,
      setLeadSessionId: state.setLeadSessionId,
      setShouldSearchNextAvailability: state.setShouldSearchNextAvailability,
      setStateCode: state.setStateCode,
      stateCodeValue: state.stateCode,
      utmParams: state.utmParameters,
      flagsHaveBeenFetched: state.flags.flagsHaveBeenFetched,
      experimentCuratedAppointmentSlotsEnabled:
        state.flags.experimentCuratedAppointmentSlotsEnabled,
      hasCuratedAppointmentSlotsFlag:
        state.flags.experimentCuratedAppointmentSlots,
      patientProfile: state.patient.patientProfile,
    })),
  );
  const { isLoggedIn } = useIsLoggedIn();
  const { title, description } = getHeaderText(utmParams?.service);

  useEffect(() => {
    if (isLoggedIn && patientProfile && !serviceTypeId) {
      setSearchParams(
        { state: patientProfile.address.stateCode },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = searchParams.get('state');

    if (state) {
      tracking.setBaseProperty({ selectedState: state });
    }
  }, [searchParams]);

  useEffect(() => {
    if (
      !flagsHaveBeenFetched ||
      !experimentCuratedAppointmentSlotsEnabled ||
      isLoggedIn
    ) {
      return;
    }

    tracking.trackExperimentStarted({
      experimentName: 'curated_appointment_slots',
      variantName: hasCuratedAppointmentSlotsFlag ? 'treatment' : 'control',
    });
  }, [
    experimentCuratedAppointmentSlotsEnabled,
    flagsHaveBeenFetched,
    hasCuratedAppointmentSlotsFlag,
    isLoggedIn,
  ]);

  // MAT experiment enrollment is fired in AvailableServices.handleClick when
  // the user actually selects an addiction-treatment service — not here on
  // services-page load. Enrolling everyone who hits the services page would
  // contaminate the analysis cohort with users who never qualified for the
  // experiment (most services-page visitors never select MOUD), inflating
  // the denominator without affecting the numerator and washing out the
  // measured effect. Bucket assignment is still deterministic per Firebase
  // installation id, so deferring enrollment doesn't bias the 50/50 split
  // within the qualifying cohort.

  useEffect(() => {
    if (!leadSessionId) {
      const builtLeadSessionId = buildLeadSessionId();
      setLeadSessionId(builtLeadSessionId);
    }
  }, [leadSessionId, setLeadSessionId]);

  useEffect(() => {
    setShouldSearchNextAvailability(true);
  }, [setShouldSearchNextAvailability]);

  function handleOnChange(event: React.ChangeEvent<HTMLSelectElement>) {
    tracking.trackEvent(Events.SELECT_STATE, { AutoSelected: false });

    setStateCode(event.currentTarget.value);
    // Trigger the route loader to fetch services data
    setSearchParams({ state: event.currentTarget.value });
  }

  function handleBackClick() {
    window.history.go(-1);
  }

  const { patientWebUrl } = usePatientWebUrl();

  return (
    <Layout
      className="flex flex-col"
      hideBackButton={!isLoggedIn}
      hideProgressBar={isLoggedIn}
      onBackClick={handleBackClick}
    >
      <div className="mb-4">
        <Heading level="h1" className="mb-1">
          {title}
        </Heading>
        <Paragraph className="text-text-medium">{description}</Paragraph>
      </div>

      {!isLoggedIn && (
        <Field className="mb-6">
          <Label>Where do you live?</Label>
          <Description className="text-text-medium">
            We’ll use this to match you with the best providers near you.
          </Description>

          <Select
            onChange={handleOnChange}
            value={stateCodeValue}
            data-testid="pw_state-select"
          >
            <option value={DEFAULT_SELECT_VALUE} disabled>
              Select a State
            </option>
            {US_STATES.map(({ name, abbreviation }) => (
              <option key={abbreviation} value={abbreviation}>
                {name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      {!utmParams?.service && (
        <AvailableServices
          getVisitTypeUrl={
            isLoggedIn ? getVisitTypeUrlForPatient : getVisitTypeUrl
          }
        />
      )}

      <div className="mt-auto">
        {utmParams?.service && (
          <Button
            className="mb-1 w-full"
            to={getVisitTypeUrl(utmParams.service)}
          >
            Next <ArrowRightIcon />
          </Button>
        )}

        {!isLoggedIn && (
          <div className="flex items-center justify-center gap-4 pt-4">
            <Paragraph>Already have an account?</Paragraph>

            <Link to={patientWebUrl}>Sign In</Link>
          </div>
        )}
      </div>
    </Layout>
  );
}
